import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { RealtimeChannel } from '@supabase/supabase-js';

import { useApp } from '@/contexts/AppContext';
import { useDentist } from '@/contexts/DentistContext';
import { Patient, getPatient } from '@/data/Patients';
import {
  Request,
  approveRequest,
  cancelRequest,
  createRequest,
  declineRequest,
  getAppointmentsForDay,
  getRequestsForDashboardByStatus,
  updateAttendanceStatus,
  updateRequestPurpose
} from '@/data/Requests';
import { useToast } from '@/hooks';
import {
  OFF_DUTY_PATIENT_ID,
  OFF_DUTY_PURPOSE,
  REQUEST_EXPIRATION_BUFFER_IN_MINUTES,
  REQUEST_STATUS_OPTIONS
} from '@/utils/constants';
import { now } from '@/utils/helpers/now';
import { parseDate } from '@/utils/helpers/parseDate';
import { parseTime } from '@/utils/helpers/parseTime';
import { supabase } from '@/utils/supabase';
import { ensureMinimumDelay } from '@/utils/timing';
import { RequestStatus } from '@/utils/types';
import {
  UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import {
  addHours,
  addMinutes,
  differenceInDays,
  endOfDay,
  format,
  isAfter,
  isBefore,
  isSameDay,
  startOfDay
} from 'date-fns';

interface RequestsContextProps {
  requests: Request[];
  setRequests: (requests: Request[]) => void;
  fetchAppointmentsForDate: (date: string) => Promise<void>;
  isDateLoaded: (date: string) => boolean;
  isDateLoading: (date: string) => boolean;
  isDateFailed: (date: string) => boolean;
  fetchRequestsByStatus: (status: RequestStatus) => Promise<void>;
  isStatusLoaded: (status: RequestStatus) => boolean;
  isStatusLoading: (status: RequestStatus) => boolean;
  isStatusFailed: (status: RequestStatus) => boolean;
  requestsByStatus: Record<RequestStatus, Request[]>;
  filteredRequests: Request[];
  pendingRequestsCount: number;
  selectedStatus: RequestStatus;
  setSelectedStatus: React.Dispatch<React.SetStateAction<RequestStatus>>;
  approveRequestMutation: UseMutationResult<
    void,
    Error,
    { request: Request },
    unknown
  >;
  declineRequestMutation: UseMutationResult<
    void,
    Error,
    { request: Request; reason: string | null },
    unknown
  >;
  cancelRequestMutation: UseMutationResult<
    void,
    Error,
    { request: Request; reason: string | null },
    unknown
  >;
  updateAttendanceStatusMutation: UseMutationResult<
    void,
    Error,
    { request: Request; status: string },
    unknown
  >;
  markAsOffDutyMutation: UseMutationResult<
    Request,
    Error,
    { date: Date; start_time?: string; end_time?: string },
    unknown
  >;
  updateRequestPurposeMutation: UseMutationResult<
    void,
    Error,
    { request: Request; purpose: string | null },
    unknown
  >;
  onUpdateStatus: (request: Request, status: RequestStatus) => void;
  onUpdateAttendanceStatus: (
    request: Request,
    attendanceStatus: string
  ) => void;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  searchedRequests: Request[];
  focusedRequestId: string | undefined;
  setFocusedRequestId: React.Dispatch<React.SetStateAction<string | undefined>>;
  updating: boolean;
}

const RequestsContext = createContext<RequestsContextProps | undefined>(
  undefined
);

interface RequestsProviderProps {
  children: ReactNode;
}

export const RequestsProvider: React.FC<RequestsProviderProps> = ({
  children
}) => {
  const displayToast = useToast();
  const { selectedDentist: dentist } = useDentist();
  const { isPaused, isResumed } = useApp();
  const queryClient = useQueryClient();

  const [selectedStatus, setSelectedStatus] = useState<RequestStatus>(
    REQUEST_STATUS_OPTIONS.PENDING
  );
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [channel, setChannel] = useState<RealtimeChannel | null>(null);
  const [focusedRequestId, setFocusedRequestId] = useState<string | undefined>(
    undefined
  );
  const [loadedDates, setLoadedDates] = useState<Set<string>>(new Set());
  const [loadingDates, setLoadingDates] = useState<Set<string>>(new Set());
  const [failedDates, setFailedDates] = useState<Set<string>>(new Set());
  const [loadedStatuses, setLoadedStatuses] = useState<Set<RequestStatus>>(
    new Set()
  );
  const [loadingStatuses, setLoadingStatuses] = useState<Set<RequestStatus>>(
    new Set()
  );
  const [failedStatuses, setFailedStatuses] = useState<Set<RequestStatus>>(
    new Set()
  );

  const markDateAsLoading = useCallback((date: string) => {
    setLoadingDates((prev) => new Set([...prev, date]));
    setLoadedDates(
      (prev) => new Set(Array.from(prev).filter((d) => d !== date))
    );
    setFailedDates(
      (prev) => new Set(Array.from(prev).filter((d) => d !== date))
    );
  }, []);
  const markDateAsLoaded = useCallback((date: string) => {
    setLoadingDates(
      (prev) => new Set(Array.from(prev).filter((d) => d !== date))
    );
    setFailedDates(
      (prev) => new Set(Array.from(prev).filter((d) => d !== date))
    );
    setLoadedDates((prev) => new Set([...prev, date]));
  }, []);
  const markDateAsFailed = useCallback((date: string) => {
    setLoadingDates(
      (prev) => new Set(Array.from(prev).filter((d) => d !== date))
    );
    setLoadedDates(
      (prev) => new Set(Array.from(prev).filter((d) => d !== date))
    );
    setFailedDates((prev) => new Set([...prev, date]));
  }, []);
  const markStatusAsLoading = useCallback((status: RequestStatus) => {
    setLoadingStatuses((prev) => new Set([...prev, status]));
    setLoadedStatuses(
      (prev) => new Set(Array.from(prev).filter((s) => s !== status))
    );
    setFailedStatuses(
      (prev) => new Set(Array.from(prev).filter((s) => s !== status))
    );
  }, []);
  const markStatusAsLoaded = useCallback((status: RequestStatus) => {
    setLoadedStatuses((prev) => new Set([...prev, status]));
    setLoadingStatuses(
      (prev) => new Set(Array.from(prev).filter((s) => s !== status))
    );
    setFailedStatuses(
      (prev) => new Set(Array.from(prev).filter((s) => s !== status))
    );
  }, []);
  const markStatusAsFailed = useCallback((status: RequestStatus) => {
    setLoadingStatuses(
      (prev) => new Set(Array.from(prev).filter((s) => s !== status))
    );
    setLoadedStatuses(
      (prev) => new Set(Array.from(prev).filter((s) => s !== status))
    );
    setFailedStatuses((prev) => new Set([...prev, status]));
  }, []);

  const { data: requests = [] } = useQuery<Request[]>({
    queryKey: ['requests', dentist?.id],
    queryFn: async () => {
      return [];
    },
    enabled: false
  });

  const setRequests = useCallback(
    (newRequests: Request[]) => {
      queryClient.setQueryData(['requests', dentist?.id], newRequests);
    },
    [queryClient, dentist?.id]
  );

  const fetchAppointmentsForDate = useCallback(
    async (date: string): Promise<void> => {
      if (!dentist?.id) return;

      try {
        markDateAsLoading(date);
        const fetchStartTime = Date.now();

        const appointments = await queryClient.fetchQuery({
          queryKey: ['appointments', dentist.id, date],
          queryFn: () => getAppointmentsForDay(dentist.id!, date),
          staleTime: 0
        });

        // Ensure minimum delay for skeleton loading
        await ensureMinimumDelay(fetchStartTime);

        queryClient.setQueryData(
          ['requests', dentist.id],
          (oldRequests: Request[] = []) => {
            const keptRequests = oldRequests.filter((request) => {
              const isSameDay = request.date === date;
              const isCalendarStatus =
                request.status === REQUEST_STATUS_OPTIONS.APPROVED ||
                request.status === REQUEST_STATUS_OPTIONS.RESCHEDULED;
              return !(isSameDay && isCalendarStatus);
            });
            return [
              ...new Map(
                [...keptRequests, ...appointments].map((request) => [
                  request.id,
                  request
                ])
              ).values()
            ];
          }
        );
        markDateAsLoaded(date);
      } catch {
        markDateAsFailed(date);
        displayToast({
          message: `Failed to load appointments for ${format(parseDate(date), 'MMM d, yyyy')}`,
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      }
    },
    [dentist?.id, queryClient, displayToast, markDateAsLoading, markDateAsLoaded, markDateAsFailed]
  );

  const isDateLoaded = useCallback(
    (date: string): boolean => {
      return loadedDates.has(date);
    },
    [loadedDates]
  );

  const isDateLoading = useCallback(
    (date: string): boolean => {
      return loadingDates.has(date);
    },
    [loadingDates]
  );

  const isDateFailed = useCallback(
    (date: string): boolean => {
      return failedDates.has(date);
    },
    [failedDates]
  );

  const fetchRequestsByStatus = useCallback(
    async (status: RequestStatus): Promise<void> => {
      if (!dentist?.id) return;

      try {
        markStatusAsLoading(status);
        const fetchStartTime = Date.now();

        const statusRequests = await queryClient.fetchQuery({
          queryKey: ['requests', dentist.id, 'status', status],
          queryFn: () =>
            getRequestsForDashboardByStatus({
              dentistId: dentist.id,
              status,
              startDate:
                status === REQUEST_STATUS_OPTIONS.PENDING
                  ? format(now(), 'yyyy-MM-dd')
                  : undefined
            }),
          staleTime: 0
        });

        // Ensure minimum delay for skeleton loading
        await ensureMinimumDelay(fetchStartTime);

        queryClient.setQueryData(
          ['requests', dentist.id],
          (oldRequests: Request[] = []) => {
            return [
              ...new Map(
                [...oldRequests, ...statusRequests].map((request) => [
                  request.id,
                  request
                ])
              ).values()
            ];
          }
        );
        markStatusAsLoaded(status);
      } catch {
        markStatusAsFailed(status);
        displayToast({
          message: `Failed to load ${status.toLowerCase()} requests`,
          duration: 3000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      }
    },
    [
      dentist?.id,
      queryClient,
      displayToast,
      markStatusAsLoading,
      markStatusAsLoaded,
      markStatusAsFailed
    ]
  );

  const isStatusLoaded = useCallback(
    (status: RequestStatus): boolean => {
      return loadedStatuses.has(status);
    },
    [loadedStatuses]
  );

  const isStatusLoading = useCallback(
    (status: RequestStatus): boolean => {
      return loadingStatuses.has(status);
    },
    [loadingStatuses]
  );

  const isStatusFailed = useCallback(
    (status: RequestStatus): boolean => {
      return failedStatuses.has(status);
    },
    [failedStatuses]
  );

  const stream = useCallback(async () => {
    if (channel) supabase.removeChannel(channel);

    if (!dentist?.id || (isPaused && !isResumed)) {
      setChannel(null);
      return;
    }

    try {
      const channelName = `requests${dentist.id?.replace(
        /-/g,
        ''
      )}${Math.floor(Date.now() / 1000)}`;
      const newChannel = supabase
        .channel(channelName)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'requests',
            filter: `dentist_id=eq.${dentist.id}`
          },
          async (payload) => {
            const newRequest = payload.new;
            const patient = await queryClient.fetchQuery({
              queryKey: ['patient', newRequest.patient_id],
              queryFn: () => getPatient(newRequest.patient_id)
            });
            queryClient.setQueryData(
              ['requests', dentist.id],
              (oldRequests: Request[] = []) => [
                ...oldRequests.filter(
                  (request) => request.id !== newRequest.id
                ),
                { ...newRequest, patient: patient as Patient } as Request
              ]
            );
          }
        )
        .subscribe();

      setChannel(newChannel);
    } catch {
      displayToast({
        message: 'Please check your internet connection.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  }, [dentist?.id, queryClient, isPaused, isResumed]);

  const approveRequestMutation = useMutation({
    mutationFn: async ({ request }: { request: Request }) => {
      return await approveRequest(request.id!);
    },
    onSuccess: (_, { request }) => {
      queryClient.setQueryData(
        ['requests', dentist?.id],
        (oldRequests: Request[] = []) =>
          oldRequests.map((r) =>
            r.id === request.id
              ? { ...r, status: REQUEST_STATUS_OPTIONS.APPROVED }
              : r
          )
      );
      displayToast({
        message: 'Request approved successfully.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    },
    onError: () => {
      displayToast({
        message: 'Failed to approve request. Please try again.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  });

  const declineRequestMutation = useMutation({
    mutationFn: async ({
      request,
      reason
    }: {
      request: Request;
      reason: string | null;
    }) => {
      return await declineRequest(request.id!, reason);
    },
    onSuccess: (_, { request }) => {
      queryClient.setQueryData(
        ['requests', dentist?.id],
        (oldRequests: Request[] = []) =>
          oldRequests.map((r) =>
            r.id === request.id
              ? { ...r, status: REQUEST_STATUS_OPTIONS.DECLINED }
              : r
          )
      );
      displayToast({
        message: 'Request declined successfully.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    },
    onError: () => {
      displayToast({
        message: 'Failed to decline request. Please try again.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  });

  const cancelRequestMutation = useMutation({
    mutationFn: async ({
      request,
      reason
    }: {
      request: Request;
      reason: string | null;
    }) => {
      return await cancelRequest(request.id!, reason);
    },
    onSuccess: (_, { request }) => {
      queryClient.setQueryData(
        ['requests', dentist?.id],
        (oldRequests: Request[] = []) =>
          oldRequests.map((r) =>
            r.id === request.id
              ? { ...r, status: REQUEST_STATUS_OPTIONS.CANCELLED }
              : r
          )
      );
      if (request.patient_id === OFF_DUTY_PATIENT_ID) {
        displayToast({
          message: `Off-duty status removed successfully.`,
          duration: 5000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      } else {
        displayToast({
          message: 'Appointment cancelled successfully.',
          duration: 5000,
          position: 'bottom',
          positionAnchor: 'tab-bar'
        });
      }
    },
    onError: () => {
      displayToast({
        message: 'Failed to cancel appointment. Please try again.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  });

  const updateAttendanceStatusMutation = useMutation({
    mutationFn: async ({
      request,
      status
    }: {
      request: Request;
      status: string;
    }) => {
      return await updateAttendanceStatus(request.id!, status);
    },
    onSuccess: (_, { request, status }) => {
      queryClient.setQueryData(
        ['requests', dentist?.id],
        (oldRequests: Request[] = []) =>
          oldRequests.map((r) =>
            r.id === request.id ? { ...r, attendance_status: status } : r
          )
      );
      displayToast({
        message:
          status === 'DEFAULT'
            ? 'Attendance status removed.'
            : `Appointment marked as ${status.toLowerCase()}.`,
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    },
    onError: () => {
      displayToast({
        message: 'Failed to update attendance status. Please try again.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  });

  const updateRequestPurposeMutation = useMutation({
    mutationFn: async ({
      request,
      purpose
    }: {
      request: Request;
      purpose: string | null;
    }) => {
      if (!request.id) throw new Error('Missing request id');
      await updateRequestPurpose(request.id, purpose);
    },
    onSuccess: (_, { request, purpose }) => {
      queryClient.setQueryData(
        ['requests', dentist?.id],
        (oldRequests: Request[] = []) =>
          oldRequests.map((r) =>
            r.id === request.id ? { ...r, purpose } : r
          )
      );
      displayToast({
        message: 'Procedures updated successfully.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    },
    onError: () => {
      displayToast({
        message: 'Failed to update procedures. Please try again.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  });

  const markAsOffDutyMutation = useMutation({
    mutationFn: async ({
      date,
      start_time,
      end_time
    }: {
      date: Date;
      start_time?: string;
      end_time?: string;
    }) => {
      if (!dentist?.id) throw new Error('No dentist selected');

      const formattedDate = format(parseDate(date), 'yyyy-MM-dd');
      const formattedStartTime = start_time
        ? format(parseTime(start_time), 'HH:mm')
        : format(startOfDay(parseDate(date)), 'HH:mm');
      const formattedEndTime = end_time
        ? format(parseTime(end_time), 'HH:mm')
        : format(endOfDay(parseDate(date)), 'HH:mm');

      const newRequest = await queryClient.fetchQuery({
        queryKey: ['createRequest', dentist.id, formattedDate],
        queryFn: () =>
          createRequest({
            dentist_id: dentist.id!,
            date: formattedDate,
            start_time: formattedStartTime,
            end_time: formattedEndTime,
            purpose: OFF_DUTY_PURPOSE,
            status: REQUEST_STATUS_OPTIONS.APPROVED,
            patient_id: OFF_DUTY_PATIENT_ID
          })
      });

      if (!newRequest) throw new Error('Failed to mark as off-duty');

      return {
        ...newRequest,
        patient: { first_name: 'Off', last_name: 'Duty' }
      } as Request;
    },
    onSuccess: (newRequest) => {
      queryClient.setQueryData(
        ['requests', dentist?.id],
        (oldRequests: Request[] = []) => [...oldRequests, newRequest]
      );
    },
    onError: (error, { date }) => {
      displayToast({
        message: `Failed to mark ${format(parseDate(date), 'MMMM d, yyyy')} as off-duty.`,
        duration: 2400,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
    }
  });

  const onUpdateStatus = useCallback(
    (request: Request, status: RequestStatus) => {
      queryClient.setQueryData(
        ['requests', dentist?.id],
        (oldRequests: Request[] = []) =>
          oldRequests.map((r) => (r.id === request.id ? { ...r, status } : r))
      );
    },
    [queryClient, dentist?.id]
  );

  const onUpdateAttendanceStatus = useCallback(
    (request: Request, attendanceStatus: string) => {
      updateAttendanceStatusMutation.mutate({
        request,
        status: attendanceStatus
      });
    },
    [updateAttendanceStatusMutation]
  );

  const requestsByStatus = useMemo(() => {
    const sortedRequests = [...requests].sort((a, b) =>
      differenceInDays(parseDate(a.date), parseDate(b.date))
    );

    return sortedRequests.reduce(
      (acc, request) => {
        if (!acc[request.status]) {
          acc[request.status] = [];
        }
        acc[request.status].push(request);
        return acc;
      },
      {} as Record<RequestStatus, Request[]>
    );
  }, [requests]);

  const filteredRequests = useMemo(() => {
    if (Object.keys(requestsByStatus).length === 0) return [];

    const expirationTime = addMinutes(
      now(),
      REQUEST_EXPIRATION_BUFFER_IN_MINUTES
    );

    return (
      requestsByStatus[selectedStatus]?.filter((request: Request) => {
        if (selectedStatus === REQUEST_STATUS_OPTIONS.PENDING) {
          const requestDateTime = parseDate(
            `${request.date}T${request.start_time}`
          );
          return !isBefore(requestDateTime, expirationTime);
        }
        return true;
      }) || []
    );
  }, [selectedStatus, requestsByStatus]);

  const formattedRequestsWithSearch = useMemo(() => {
    return requests.map((request) => ({
      ...request,
      searchString: [
        format(parseDate(request.date), 'MMMM d, yyyy'),
        format(parseTime(request.start_time), 'h:mm a'),
        format(parseTime(request.end_time), 'h:mm a'),
        `${request.patient?.first_name} ${request.patient?.last_name}`,
        `0${request.patient?.mobile_number}`,
        request.hmo_provider
      ]
        .join('')
        .toLowerCase()
        .replace(/\s+/g, '')
    }));
  }, [requests]);

  const searchedRequests = useMemo(() => {
    if (searchQuery === '') return filteredRequests;
    const normalizedQuery = searchQuery.toLowerCase().replace(/\s+/g, '');
    return filteredRequests.filter((request) => {
      const formatted = formattedRequestsWithSearch.find(
        (r) => r.id === request.id
      );
      return formatted?.searchString.includes(normalizedQuery);
    });
  }, [searchQuery, filteredRequests, formattedRequestsWithSearch]);

  const pendingRequestsCount = useMemo(() => {
    if (Object.keys(requestsByStatus).length === 0) return 0;

    const currentDateAndTime = now();
    return (
      requestsByStatus[REQUEST_STATUS_OPTIONS.PENDING]?.filter(
        (request: Request) => {
          const requestDate = parseDate(request.date);
          const requestStartTime = parseDate(
            `${request.date}T${request.start_time}`
          );

          return !(
            isBefore(requestDate, startOfDay(currentDateAndTime)) ||
            (isSameDay(requestDate, currentDateAndTime) &&
              isAfter(addHours(currentDateAndTime, 1.5), requestStartTime))
          );
        }
      )?.length || 0
    );
  }, [requestsByStatus]);

  const updating =
    cancelRequestMutation.isPending ||
    updateAttendanceStatusMutation.isPending ||
    approveRequestMutation.isPending ||
    declineRequestMutation.isPending ||
    markAsOffDutyMutation.isPending ||
    updateRequestPurposeMutation.isPending;

  /* Reset the states when the dentist changes */
  useEffect(() => {
    setSelectedStatus(REQUEST_STATUS_OPTIONS.PENDING);
    setLoadedDates(new Set());
    setLoadingDates(new Set());
    setFailedDates(new Set());
    setLoadedStatuses(new Set());
    setLoadingStatuses(new Set());
    setFailedStatuses(new Set());
    setSearchQuery('');
  }, [dentist?.id]);

  useEffect(() => {
    stream();
  }, [stream]);

  useEffect(() => {
    if (!isResumed || !dentist?.id) return;

    setLoadedDates(new Set());
    setFailedDates(new Set());
    setLoadedStatuses(new Set());
    setFailedStatuses(new Set());

    queryClient.removeQueries({ queryKey: ['appointments', dentist.id] });
    queryClient.removeQueries({
      queryKey: ['requests', dentist.id, 'status']
    });
  }, [isResumed, dentist?.id, queryClient]);

  useEffect(() => {
    if (
      isStatusLoaded(REQUEST_STATUS_OPTIONS.PENDING) ||
      isStatusLoading(REQUEST_STATUS_OPTIONS.PENDING) ||
      isStatusFailed(REQUEST_STATUS_OPTIONS.PENDING)
    ) {
      return;
    }

    fetchRequestsByStatus(REQUEST_STATUS_OPTIONS.PENDING);
  }, [
    fetchRequestsByStatus,
    isStatusLoaded,
    isStatusLoading,
    isStatusFailed
  ]);

  return (
    <RequestsContext.Provider
      value={{
        requests,
        setRequests,
        fetchAppointmentsForDate,
        isDateLoaded,
        isDateLoading,
        isDateFailed,
        fetchRequestsByStatus,
        isStatusLoaded,
        isStatusLoading,
        isStatusFailed,
        requestsByStatus,
        filteredRequests,
        pendingRequestsCount,
        selectedStatus,
        setSelectedStatus,
        approveRequestMutation,
        declineRequestMutation,
        cancelRequestMutation,
        updateAttendanceStatusMutation,
        markAsOffDutyMutation,
        updateRequestPurposeMutation,
        onUpdateStatus,
        onUpdateAttendanceStatus,
        searchQuery,
        setSearchQuery,
        searchedRequests,
        focusedRequestId,
        setFocusedRequestId,
        updating
      }}
    >
      {children}
    </RequestsContext.Provider>
  );
};

export const useRequests = () => {
  const context = useContext(RequestsContext);
  if (context === undefined) {
    throw new Error('useRequests must be used within a RequestsProvider');
  }
  return context;
};
